import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import { auth } from '../firebase';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn(
    '[Supabase] Variáveis de ambiente não configuradas. Configure REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY no .env'
  );
}

/**
 * Retorna o Firebase ID token atual (string) ou null se não autenticado.
 *
 * - Usado pelo client Supabase para cada request REST (via accessToken callback).
 * - Usado pelo Realtime via supabase.realtime.setAuth() ao detectar login/logout.
 *
 * O Firebase SDK gerencia o cache + refresh do token. getIdToken() retorna o token
 * em cache se ainda for válido (5+ min para expirar); caso contrário força refresh.
 */
const getFirebaseIdToken = async () => {
  try {
    const user = auth?.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch (e) {
    logger.warn('[Supabase] Falha ao obter Firebase ID token:', e?.message);
    return null;
  }
};

// Cliente principal — usado no frontend com permissões do usuário (Row Level Security)
// Fase 3 LGPD/Segurança (12/05/2026): Third-Party Auth Firebase → identidade do usuário
// chega ao Supabase. Policies RLS podem usar auth.uid() e auth.jwt() corretamente.
export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        // accessToken: Supabase chama essa função antes de cada request REST.
        // Retorna o JWT do Firebase, que é validado pelo Supabase (TPA habilitado).
        accessToken: getFirebaseIdToken,
        auth: {
          // Sessão é gerenciada pelo Firebase, não pelo Supabase.
          persistSession: false,
          autoRefreshToken: false,
        },
        realtime: {
          params: {
            eventsPerSecond: 10,
          },
        },
      })
    : null;

/**
 * Sincroniza o token Realtime com o Firebase Auth.
 *
 * O canal Realtime do Supabase mantém uma conexão WebSocket persistente. Ele
 * precisa que o token de auth seja setado explicitamente via realtime.setAuth(),
 * pois não usa o accessToken callback.
 *
 * Comportamento:
 * - Em login: pega o token e seta no Realtime.
 * - Em refresh do token: detecta via onIdTokenChanged e atualiza.
 * - Em logout: limpa.
 */
let _realtimeAuthAttached = false;

export const attachRealtimeAuth = () => {
  if (_realtimeAuthAttached || !supabase || !auth) return;
  _realtimeAuthAttached = true;

  try {
    auth.onIdTokenChanged(async (user) => {
      try {
        if (!user) {
          // Logout — limpa auth do Realtime
          supabase.realtime.setAuth(null);
          return;
        }
        const token = await user.getIdToken();
        supabase.realtime.setAuth(token);
        logger.log('[Supabase] Realtime auth sincronizado com Firebase');
      } catch (e) {
        logger.warn('[Supabase] Falha ao sincronizar Realtime auth:', e?.message);
      }
    });
  } catch (e) {
    logger.warn('[Supabase] onIdTokenChanged falhou:', e?.message);
  }
};

// Anexa o listener assim que o módulo carrega.
attachRealtimeAuth();

// 🔧 DEBUG: expõe client em window pra inspecionar canais Realtime no console.
// Seguro porque o anon key já é público (vai no bundle JS) e RLS protege os dados.
if (typeof window !== 'undefined' && supabase) {
  window.__supabase = supabase;
}

/**
 * Retorna o client Supabase. Lança erro se não estiver configurado.
 * Use esta função para garantir que o client está pronto antes de usar.
 */
export const getSupabase = () => {
  if (!supabase) {
    throw new Error(
      '[Supabase] Client não inicializado. Verifique as variáveis de ambiente.'
    );
  }
  return supabase;
};

export default supabase;
